import { RefObject, useEffect } from 'react';

import { ZoomController } from '../client/zoomController';
import {
    Tap, distance, doubleTapTarget, isDoubleTap, midpoint, pinchZoom,
} from '../helpers/gestures';

const PINCH_TOUCHES = 2;

const toPoint = (touch: Touch): { x: number; y: number } => ({ x: touch.clientX, y: touch.clientY });

const useTouchZoom = (
    scrollerRef: RefObject<HTMLElement>,
    controller: ZoomController | null,
    enabled: boolean,
): void => {
    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!enabled || !scroller || !controller) {
            return undefined;
        }
        let pinch: { zoom: number; distance: number } | null = null;
        let lastTap: Tap | null = null;
        let moved = false;

        const anchor = (x: number, y: number): { x: number; y: number } => {
            const rect = scroller.getBoundingClientRect();
            return { x: x - rect.left, y: y - rect.top };
        };

        const onStart = (event: TouchEvent): void => {
            moved = false;
            if (event.touches.length === PINCH_TOUCHES) {
                pinch = {
                    zoom: controller.zoom,
                    distance: distance(toPoint(event.touches[0]), toPoint(event.touches[1])),
                };
                lastTap = null;
            }
        };

        const onMove = (event: TouchEvent): void => {
            moved = true;
            if (!pinch || event.touches.length !== PINCH_TOUCHES) {
                return;
            }
            event.preventDefault();
            const a = toPoint(event.touches[0]);
            const b = toPoint(event.touches[1]);
            const center = midpoint(a, b);
            const target = anchor(center.x, center.y);
            controller.pinchZoom(pinchZoom(pinch.zoom, pinch.distance, distance(a, b)), target.x, target.y);
        };

        const onEnd = (event: TouchEvent): void => {
            if (pinch) {
                if (event.touches.length < PINCH_TOUCHES) {
                    pinch = null;
                }
                lastTap = null;
                return;
            }
            const touch = event.changedTouches[0];
            if (moved || event.touches.length || !touch) {
                lastTap = null;
                return;
            }
            const tap: Tap = { x: touch.clientX, y: touch.clientY, time: event.timeStamp };
            if (isDoubleTap(lastTap, tap)) {
                lastTap = null;
                const point = anchor(tap.x, tap.y);
                const fitWidth = controller.fitZoom('width');
                const target = doubleTapTarget(controller.zoom, fitWidth);
                controller.animateZoom(target, target === fitWidth ? 'width' : null, point);
            } else {
                lastTap = tap;
            }
        };

        scroller.addEventListener('touchstart', onStart, { passive: true });
        scroller.addEventListener('touchmove', onMove, { passive: false });
        scroller.addEventListener('touchend', onEnd, { passive: true });
        scroller.addEventListener('touchcancel', onEnd, { passive: true });
        return () => {
            scroller.removeEventListener('touchstart', onStart);
            scroller.removeEventListener('touchmove', onMove);
            scroller.removeEventListener('touchend', onEnd);
            scroller.removeEventListener('touchcancel', onEnd);
        };
    }, [scrollerRef, controller, enabled]);
};

export default useTouchZoom;
